import { useDocumentEdit, QuoteEditData, QuoteEditItem } from './useDocumentEdit';
import { quotesAPI } from '../utils/api';

// Ré-exporter les types pour compatibilité
export type { QuoteEditData, QuoteEditItem };

/**
 * Hook pour gérer l'édition d'un devis
 * Wrapper autour de useDocumentEdit pour la compatibilité
 *
 * @param initialData - Données initiales du devis
 * @param vatRegime - Régime de TVA (optionnel)
 * @param onSuccess - Callback appelé après sauvegarde réussie avec l'ID du devis
 * @param defaultTaxRate - Taux de TVA par défaut pour initialiser les nouveaux items
 */
export function useQuoteEdit(
  initialData: QuoteEditData,
  vatRegime?: string,
  onSuccess?: (quoteId: string) => void,
  defaultTaxRate?: { id: string; rate: number }
) {
  const result = useDocumentEdit<QuoteEditData>(
    {
      documentType: 'quote',
      api: quotesAPI,
      route: '/quotes',
      numberField: 'quoteNumber',
      dateEndField: 'validUntil',
      successMessages: {
        created: 'Devis créé avec succès',
        updated: 'Devis mis à jour avec succès',
      },
    },
    initialData,
    vatRegime,
    onSuccess,
    defaultTaxRate
  );

  // Retourner avec des noms compatibles
  return {
    quoteData: result.documentData,
    isSaving: result.isSaving,
    hasChanges: result.hasChanges,
    updateField: result.updateField,
    updateDiscountType: result.updateDiscountType,
    updateDiscountValue: result.updateDiscountValue,
    updateItem: result.updateItem,
    addItem: result.addItem,
    removeItem: result.removeItem,
    addSectionTitle: result.addSectionTitle,
    addComment: result.addComment,
    addSubtotal: result.addSubtotal,
    addCompositeLines: result.addCompositeLines,
    addProductFromCatalog: result.addProductFromCatalog,
    saveQuote: result.saveDocument,
  };
}
